import React from 'react';
import {
    DetailsIconsOptions,
    IconAuditTrail,
    IconChipCircleTools,
    IconDeskOfficeTable,
    IconClockTable,
    IconDocumentPencil,
    IconEmployeeInside,
    IconExitDevice,
    IconGuardTitle,
    IconMarkerTable,
    IconScheduleTable,
    ImgCheckInHeader, NumericFieldChip,
    PersonChip,
} from '../../../../components/buttons/IconButtons';
import ResumeInfoCard from '../../../../components/ResumeInfoCard';
import CustomModal from '../../../../components/Modal/CustomModal';
import CustomCard from '../../../../components/CustomCard';
import CustomChip from '../../../../components/CustomChip';
import CustomTooltip from '../../../../components/CustomTooltip';
import ButtonAction from '../../../../components/buttons/ButtonAction';
import CheckInLogModalApp from '../Functionality/CheckInLogModalApp';
import {
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    getDateFormat,
    getTimeFormat,
} from '../../../../Apis/functions';
import { status } from '../../../../enums/statusEnum';
import { statusBorderColor, statusColor, statusFontColor } from '../../../../enums/statusColorEnum';
import NotInformation from "../../../../components/NotInformation";
import { URL } from '../../../../Apis/Constants';
import AccessMethodIcon from '../../../../components/icons/AccessMethodIcon';
import { accessMethod } from '../../../../enums/accessMethodEnum';

/**
 * @component CheckInLogModal
 * @description Check-in log of a contractor employee entry, opened from the contractors tab.
 *
 * Shows the employee the entry belongs to, when the entry happened, who authorized it and which
 * materials came in with them.
 *
 * @param {boolean} show - Whether the modal is open.
 * @param {Function} onHide - Closes the modal.
 * @param {Object} itemSelected - Row selected in the table, its id is the equipment access log id.
 * @param {Function} setCheckOutModal - Opens the confirmation of the exit.
 * @param {string} display - Display classes, used to hide this modal while the confirmation is open.
 * @param {Function} t - Translation function.
 */
const CheckInLogModal = ({ show, onHide, itemSelected, setCheckOutModal, display, t }) => {
    const { detail, canCheckOut } = CheckInLogModalApp({ itemSelected });

    // Zone the entry was registered on, the row of the table already carries the
    // zone and its parent when the detail travels without them
    const parentZoneName = detail?.parentZoneName || itemSelected?.parentZoneName;
    const zoneName = detail?.zoneName || itemSelected?.zoneName;
    const accessMethodId = detail?.accessMethodId || itemSelected?.accessMethodId;

    return (
        <CustomModal
            show={show}
            size='lg'
            onHide={onHide}
            display={display}
            confirmButton={false}
            cancelButton={false}
            topLeftHeader={
                <>
                    <div className={'d-flex align-items-center pb-3'}>
                        <DetailsIconsOptions className='icon-color-general-950' />
                        <p className={'fs-modal-title font-color-general-950 ibl-font-w-500 ml-2'}>{t('details')}</p>
                    </div>
                </>
            }
            classNameBody='pt-2'
        >
            <CustomCard classCard={''} classContent={'p-0'}>
                <ImgCheckInHeader className={'w-100'} />
                <div className={'row'}>
                    <div className={'col-12 my-4'}>
                        <div className={'d-flex align-items-center minw-0'}>
                            {detail?.image ? (
                                <img
                                    className='h-32px w-32px object-fit-cover rounded-circle'
                                    src={`data:image/png;base64,${detail.image}`}
                                    alt={detail?.fullName}
                                />
                            ) : (
                                <PersonChip className='h-32px w-32px' />
                            )}
                            <CustomTooltip
                                text={detail?.fullName || EMPTY_VALUES.DEFAULT}
                                classes={'font-color-general-950 fs-14px ml-2 text-ellipsis'}
                                showIfTruncated={true}
                                placement={'top'}
                            />
                            <div className={"d-flex justify-content-end ml-auto"}>
                                <CustomChip
                                    className={'ml-2'}
                                    background={statusColor[detail?.statusId]}
                                    fontColor={statusFontColor[detail?.statusId]}
                                    borderColor={statusBorderColor[detail?.statusId]}
                                    legend={t(status[detail?.statusId]) || EMPTY_VALUES.DEFAULT}
                                    variant={"large"}
                                />
                            </div>

                        </div>
                    </div>
                </div>

                <div className={'border-bottom-shades-300'} />
                <div className='m-3'>
                    <ResumeInfoCard
                        title={t('trail')}
                        titleIcon={<IconAuditTrail width={24} height={24} />}
                        titleActions={
                            canCheckOut && (
                                <ButtonAction
                                    id='btn_cilm_check_out'
                                    name='check_out'
                                    className='btn-outlined-secondary-600-25'
                                    Icon={IconExitDevice}
                                    splitName={false}
                                    onClick={() => setCheckOutModal(true)}
                                />
                            )
                        }
                        icon={<IconEmployeeInside className='btn-icon-svg-24px' />}
                        iconClass='background-primary-100'
                        contentClass={'py-3'}
                        body={
                            <>
                                <div className='d-flex flex-column'>
                                    <div className='d-flex flex-column'>
                                        <p className='font-color-general-950 fs-12px ibl-font-w-600'>{t('check_in_log')}</p>

                                        <div className='row mt-1 '>
                                            <div className='col-6 d-flex align-items-center mx-0 p-0 '>
                                                <IconScheduleTable className='icon-color-general-950' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {getDateFormat(
                                                        detail?.accessDate,
                                                        false,
                                                        DATE_TIME_FORMAT_NAME_MODEL.shortMonth,
                                                    )}
                                                </p>
                                            </div>

                                            <div className='col-6 d-flex align-items-center mx-0 p-0'>
                                                <IconClockTable className='icon-color-general-950' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {getTimeFormat(detail?.accessDate)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className='row my-3 mx-0'>
                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('guard')}
                                            </p>

                                            <div className='d-flex align-items-center'>
                                                <IconGuardTitle className='icon-color-general-950 h-16px w-16px' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {detail?.guardFullName || EMPTY_VALUES.DEFAULT}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('contractor')}
                                            </p>

                                            <div className='d-flex align-items-center'>
                                                <IconDocumentPencil className='icon-color-general-950 h-16px w-16px' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {detail?.contractorName || EMPTY_VALUES.DEFAULT}
                                                </p>
                                            </div>
                                        </div>

                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0 mt-3'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('access_zone')}
                                            </p>

                                            {/* Both zones on the same line, each one with the icon of the
                                                column of the table, the dot only separates them when the
                                                two of them arrive */}
                                            <div className='d-flex align-items-center'>
                                                <IconMarkerTable className='icon-color-general-950 h-16px w-16px' />
                                                <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                    {parentZoneName || EMPTY_VALUES.DEFAULT}
                                                </p>

                                                {parentZoneName && zoneName &&
                                                    <span className='font-color-general-950 fs-14px mx-2'>•</span>
                                                }

                                                {zoneName &&
                                                    <>
                                                        <IconDeskOfficeTable className='icon-color-general-950 h-16px w-16px' />
                                                        <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                            {zoneName}
                                                        </p>
                                                    </>
                                                }
                                            </div>
                                        </div>

                                        {/* Method the entry was registered with, the row of the table
                                            already carries it when the detail travels without it */}
                                        <div className='col-sm-12 col-md-6 d-flex flex-column mx-0 p-0 mt-3'>
                                            <p className='font-color-general-950 fs-12px ibl-font-w-600'>
                                                {t('access_method')}
                                            </p>

                                            <div className='d-flex align-items-center'>
                                                {accessMethodId ? (
                                                    <>
                                                        <AccessMethodIcon
                                                            className='icon-color-general-950 h-16px w-16px'
                                                            AccessMethodId={accessMethodId}
                                                        />
                                                        <p className='font-color-general-950 fs-14px ml-2 mb-0'>
                                                            {t(accessMethod[accessMethodId])}
                                                        </p>
                                                    </>
                                                ) : (
                                                    <p className='font-color-general-950 fs-14px mb-0'>
                                                        {EMPTY_VALUES.DEFAULT}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className={'border-bottom-shades-300'} />
                                <p className={'font-color-general-950 fs-12px ibl-font-w-600 mt-3 mb-2'}>
                                    {t('materials_associate')}
                                </p>

                                    {detail?.materials?.length ? (
                                        detail.materials.map((material) => (
                                            <div  key={material?.id} className='border-full-shades-200 background-shades-100 border-radius-8px mb-3'>
                                                <div className='row align-items-center my-1'>
                                                    <div className='col'>
                                                        <div className='d-flex align-items-center'>
                                                            {material?.fileUploadDto ? (
                                                                <img
                                                                    className='h-32px w-32px border-radius-8px'
                                                                    src={URL + material.fileUploadDto.filePath}
                                                                    alt={material?.name}
                                                                />
                                                            ) : (
                                                                <IconChipCircleTools className='h-32px w-32px icon-color-general-950' />
                                                            )}
                                                            <CustomTooltip
                                                                text={material?.name || EMPTY_VALUES.DEFAULT}
                                                                classes={
                                                                    'font-color-general-950 ibl-font-w-600 fs-12px ml-2 text-ellipsis'
                                                                }
                                                                showIfTruncated={true}
                                                                placement={'top'}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className='col-auto'>
                                                        <div className={"d-flex align-items-center"}>
                                                            <div className={"mr-2"}><NumericFieldChip className={"icon-color-shades-500"}/></div>
                                                            <span className='font-color-shades-600 ibl-font-w-400 fs-12px'>
                                                                {material?.code || EMPTY_VALUES.DEFAULT}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <NotInformation text={t('no_records_available')} card={true} />
                                    )}

                            </>
                        }
                    />
                </div>
            </CustomCard>
        </CustomModal>
    );
};

export default CheckInLogModal;
